import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type ExternalViewerDialogState = {
    title?: string | null;
    fileId?: string | null;
    privateFileId?: string | null;
    src?: string | null;
    reservationId?: string | null;
    paymentPlanId?: string | null;
    projectId?: string | null;
};

@Injectable({ providedIn: 'root' })
export class ExternalViewerDialogService {
    private readonly stateSubject = new BehaviorSubject<ExternalViewerDialogState | null>(null);
    readonly state$ = this.stateSubject.asObservable();

    // Emits when the viewer closes, carrying the state that was open. Consumers
    // (e.g. the reservations list) use this to refresh signing status the moment
    // a signing dialog is dismissed, so the section flips to "signed" promptly.
    private readonly closedSubject = new Subject<ExternalViewerDialogState>();
    readonly closed$ = this.closedSubject.asObservable();

    isOpen(): boolean {
        return Boolean(this.stateSubject.value);
    }

    open(state: ExternalViewerDialogState): void {
        const normalized: ExternalViewerDialogState = {
            title: state.title ?? null,
            fileId: state.fileId ?? null,
            privateFileId: state.privateFileId ?? null,
            src: this.normalizeStratusUrl(state.src ?? null),
            reservationId: state.reservationId ?? null,
            paymentPlanId: state.paymentPlanId ?? null,
            projectId: state.projectId ?? null
        };

        if (!normalized.fileId && !normalized.privateFileId && !normalized.src && !normalized.reservationId) return;
        this.stateSubject.next(normalized);
    }

    openPrivateFile(fileId: string, title?: string, projectId?: string): void {
        const id = String(fileId || '').trim();
        if (!id) return;
        this.open({ privateFileId: id, title: title ?? null, projectId: projectId ?? null });
    }

    openFromUrl(url: string | null | undefined, title?: string, projectId?: string): void {
        const src = String(url || '').trim();
        if (!src) return;

        const normalizedSrc = this.normalizeStratusUrl(src);
        const fileId = this.extractFileIdFromStratusUrl(normalizedSrc);
        if (fileId) {
            this.open({ fileId, title: title ?? null, projectId: projectId ?? null });
            return;
        }

        this.open({ src: normalizedSrc, title: title ?? null, projectId: projectId ?? null });
    }

    close(): void {
        const prev = this.stateSubject.value;
        this.stateSubject.next(null);
        if (prev) this.closedSubject.next(prev);
    }

    private extractFileIdFromStratusUrl(value: string | null): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        try {
            const url = new URL(raw);
            const path = url.pathname || '';
            const match =
                path.match(/\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i)
                || path.match(/\/public\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i)
                || path.match(/\/f\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i);
            return match?.[1] ? String(match[1]) : null;
        } catch {
            return null;
        }
    }

    private normalizeStratusUrl(value: string | null): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        try {
            const url = new URL(raw);
            const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
            const isDevHost =
                currentHost === 'localhost'
                || currentHost === '127.0.0.1'
                || currentHost === '0.0.0.0'
                || currentHost.startsWith('dev.');

            const host = url.hostname.toLowerCase();
            const stratusSuffix = '.zohostratus.com';
            if (host.endsWith(stratusSuffix)) {
                const base = host.slice(0, -stratusSuffix.length).replace(/-development$/i, '');
                url.hostname = isDevHost ? `${base}-development${stratusSuffix}` : `${base}${stratusSuffix}`;
            }
            return url.toString();
        } catch {
            return raw;
        }
    }
}
